import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Actions } from 'src/model/enum/enums';
import useProgress from '../data/useProgress';

const TRACKED_PAGES = [
    '/challenges',
    '/learn',
    '/wordlists',
    '/chat',
    '/lessons',
    '/scenario',
    '/search'
];

const useTimeTracking = () => {
    const location = useLocation();
    const startTimeRef = useRef<number>(Date.now());
    const { saveProgress } = useProgress();
    const previousPathRef = useRef<string>(location.pathname);

    const isPageTracked = useCallback((path: string) => {
        return TRACKED_PAGES.some(page => path.startsWith(page));
    }, []);

    const sendTimeSpent = useCallback(() => {
        const currentTime = Date.now();
        const timeSpent = Math.floor((currentTime - startTimeRef.current) / 1000); // Convert to seconds

        if (timeSpent > 0 && isPageTracked(previousPathRef.current)) {
            saveProgress({
                Action: Actions.AddSeconds,
                Value: timeSpent.toString()
            });
        }

        // Reset the timer
        startTimeRef.current = currentTime;
    }, [saveProgress, isPageTracked]);

    useEffect(() => {
        // Only send time when actually changing pages
        if (previousPathRef.current !== location.pathname) {
            if (isPageTracked(previousPathRef.current)) {
                sendTimeSpent();
            }
            previousPathRef.current = location.pathname;
            startTimeRef.current = Date.now();
        }

        // Handle page close/refresh
        const handleBeforeUnload = () => {
            if (isPageTracked(location.pathname)) {
                sendTimeSpent();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [location.pathname, isPageTracked, sendTimeSpent]);

    return {
        sendTimeSpent
    };
};

export default useTimeTracking; 